interface RegionBoundary {
  name: string
  bounds: {
    north: number
    south: number
    east: number
    west: number
  }
}

interface StateRegion {
  state: string
  country: string
  regions: RegionBoundary[]
}

interface LocationResult {
  country: string
  state: string
  city: string
  region: string
}

// Australian State and Territory Regions - Official TRA Tourism Regions 2024
const australianRegions: StateRegion[] = [
  {
    state: 'New South Wales',
    country: 'Australia',
    regions: [
      {
        name: 'Blue Mountains',
        bounds: { north: -33.3, south: -33.9, east: 150.8, west: 150.0 }
      },
      {
        name: 'Capital Country',
        bounds: { north: -34.0, south: -35.5, east: 150.0, west: 148.5 }
      },
      {
        name: 'Central Coast',
        bounds: { north: -33.0, south: -33.5, east: 151.5, west: 151.0 }
      },
      {
        name: 'Central NSW',
        bounds: { north: -32.0, south: -34.0, east: 150.0, west: 147.0 }
      },
      {
        name: 'Hunter',
        bounds: { north: -32.0, south: -33.2, east: 151.8, west: 150.5 }
      },
      {
        name: 'New England North West',
        bounds: { north: -28.5, south: -31.0, east: 152.0, west: 150.5 }
      },
      {
        name: 'North Coast NSW',
        bounds: { north: -28.2, south: -32.8, east: 153.6, west: 152.0 }
      },
      {
        name: 'Outback NSW',
        bounds: { north: -28.0, south: -34.0, east: 148.0, west: 140.9 }
      },
      {
        name: 'Riverina',
        bounds: { north: -33.5, south: -36.0, east: 148.0, west: 144.0 }
      },
      {
        name: 'Snowy Mountains',
        bounds: { north: -35.5, south: -37.0, east: 149.5, west: 148.0 }
      },
      {
        name: 'South Coast',
        bounds: { north: -34.8, south: -37.5, east: 150.5, west: 149.5 }
      },
      {
        name: 'Sydney',
        bounds: { north: -33.4, south: -34.2, east: 151.4, west: 150.5 }
      },
      {
        name: 'The Murray',
        bounds: { north: -35.0, south: -36.0, east: 147.0, west: 142.0 }
      }
    ]
  },
  {
    state: 'Victoria',
    country: 'Australia',
    regions: [
      {
        name: 'Melbourne',
        // Extended east to 145.5 to include all southeastern suburbs (Caulfield, Bentleigh, McKinnon, etc.)
        bounds: { north: -37.5, south: -38.5, east: 145.5, west: 144.3 }
      },
      {
        name: 'Daylesford & the Macedon Ranges',
        bounds: { north: -37.0, south: -37.5, east: 144.5, west: 143.8 }
      },
      {
        name: 'Yarra Valley',
        // Includes Eltham, Hurstbridge, Kinglake, Toolangi, Healesville, Yarra Glen
        bounds: { north: -37.2, south: -37.8, east: 145.7, west: 145.0 }
      },
      {
        name: 'Yarra Ranges',
        // Includes Lilydale, Seville, Warburton, Yarra Junction, Mount Evelyn
        bounds: { north: -37.5, south: -38.0, east: 146.193, west: 145.6 }
      },
      {
        name: 'Dandenong Ranges',
        // Includes Olinda, Sassafras, Mount Dandenong, Belgrave, Kallista
        // Adjusted to start at 145.35 to avoid overlap with Melbourne metro area
        bounds: { north: -37.8, south: -38.0, east: 145.6, west: 145.35 }
      },
      {
        name: 'Mornington Peninsula',
        bounds: { north: -38.1, south: -38.6, east: 145.3, west: 144.7 }
      },
      {
        name: 'Phillip Island',
        bounds: { north: -38.3, south: -38.7, east: 145.6, west: 145.0 }
      },
      {
        name: 'Geelong and the Bellarine',
        bounds: { north: -37.8, south: -38.5, east: 144.3, west: 143.8 }
      },
      {
        name: 'Great Ocean Road',
        bounds: { north: -37.8, south: -39.2, east: 143.8, west: 140.9 }
      },
      {
        name: 'Goldfields',
        bounds: { north: -36.5, south: -37.8, east: 144.8, west: 143.2 }
      },
      {
        name: 'Grampians',
        bounds: { north: -36.5, south: -37.8, east: 143.2, west: 140.9 }
      },
      {
        name: 'High Country',
        bounds: { north: -36.0, south: -37.5, east: 149.8, west: 145.8 }
      },
      {
        name: 'Gippsland',
        bounds: { north: -37.0, south: -39.2, east: 149.8, west: 146.193 }
      },
      {
        name: 'Murray',
        bounds: { north: -35.0, south: -36.0, east: 146.0, west: 140.9 }
      },
      {
        name: 'Mallee',
        bounds: { north: -34.0, south: -35.0, east: 149.8, west: 140.9 }
      },
      {
        name: 'Wimmera',
        bounds: { north: -35.0, south: -36.5, east: 143.0, west: 140.9 }
      },
      {
        name: 'Goulburn River Valley',
        bounds: { north: -36.5, south: -37.5, east: 145.8, west: 144.5 }
      }
    ]
  },
  {
    state: 'Queensland',
    country: 'Australia',
    regions: [
      {
        name: 'Brisbane',
        bounds: { north: -27.0, south: -27.8, east: 153.4, west: 152.6 }
      },
      {
        name: 'Bundaberg',
        bounds: { north: -24.5, south: -25.5, east: 152.8, west: 151.8 }
      },
      {
        name: 'Capricorn Coast',
        bounds: { north: -22.5, south: -24.0, east: 151.5, west: 149.0 }
      },
      {
        name: 'Fraser Coast',
        bounds: { north: -25.0, south: -26.0, east: 153.2, west: 152.0 }
      },
      {
        name: 'Gladstone',
        bounds: { north: -23.5, south: -24.5, east: 151.8, west: 150.5 }
      },
      {
        name: 'Gold Coast',
        bounds: { north: -27.7, south: -28.5, east: 153.6, west: 153.0 }
      },
      {
        name: 'Mackay',
        bounds: { north: -20.5, south: -22.0, east: 149.5, west: 148.0 }
      },
      {
        name: 'Outback Queensland',
        bounds: { north: -20.0, south: -29.0, east: 148.0, west: 138.0 }
      },
      {
        name: 'Queensland Country',
        bounds: { north: -24.0, south: -29.0, east: 153.0, west: 148.0 }
      },
      {
        name: 'Sunshine Coast',
        bounds: { north: -26.0, south: -27.0, east: 153.4, west: 152.8 }
      },
      {
        name: 'Townsville',
        bounds: { north: -19.0, south: -20.0, east: 147.0, west: 145.5 }
      },
      {
        name: 'Tropical North Queensland',
        bounds: { north: -12.0, south: -19.0, east: 145.8, west: 142.0 }
      },
      {
        name: 'Whitsundays',
        bounds: { north: -20.0, south: -21.0, east: 149.5, west: 148.5 }
      }
    ]
  },
  {
    state: 'Queensland',
    country: 'Australia',
    regions: [
      {
        name: 'South East Queensland',
        bounds: { north: -26.0, south: -28.5, east: 153.6, west: 151.5 }
      },
      {
        name: 'Darling Downs',
        bounds: { north: -26.8, south: -28.5, east: 152.0, west: 149.5 }
      },
      {
        name: 'Wide Bay–Burnett',
        bounds: { north: -24.5, south: -27.0, east: 153.0, west: 150.0 }
      },
      {
        name: 'Central Queensland',
        bounds: { north: -22.0, south: -26.0, east: 151.0, west: 147.0 }
      },
      {
        name: 'Mackay, Isaac and Whitsunday',
        bounds: { north: -20.0, south: -23.0, east: 149.5, west: 147.0 }
      },
      {
        name: 'North Queensland',
        bounds: { north: -16.0, south: -20.0, east: 146.5, west: 140.0 }
      },
      {
        name: 'Far North Queensland',
        bounds: { north: -10.4, south: -16.0, east: 145.8, west: 142.0 }
      }
    ]
  },
  {
    state: 'South Australia',
    country: 'Australia',
    regions: [
      {
        name: 'Adelaide',
        bounds: { north: -34.6, south: -35.2, east: 138.8, west: 138.2 }
      },
      {
        name: 'Adelaide Hills',
        bounds: { north: -34.6, south: -35.2, east: 139.2, west: 138.8 }
      },
      {
        name: 'Barossa',
        bounds: { north: -34.3, south: -34.7, east: 139.2, west: 138.8 }
      },
      {
        name: 'Clare Valley',
        bounds: { north: -33.6, south: -34.1, east: 138.8, west: 138.4 }
      },
      {
        name: 'Eyre Peninsula',
        bounds: { north: -32.5, south: -34.8, east: 137.2, west: 135.0 }
      },
      {
        name: 'Fleurieu Peninsula',
        bounds: { north: -35.2, south: -35.8, east: 138.8, west: 138.2 }
      },
      {
        name: 'Flinders Ranges and Outback',
        bounds: { north: -26.0, south: -33.0, east: 140.0, west: 129.0 }
      },
      {
        name: 'Kangaroo Island',
        bounds: { north: -35.6, south: -36.1, east: 137.8, west: 136.6 }
      },
      {
        name: 'Limestone Coast',
        bounds: { north: -36.5, south: -38.0, east: 141.0, west: 139.5 }
      },
      {
        name: 'Murray River',
        bounds: { north: -33.8, south: -35.5, east: 140.8, west: 138.5 }
      },
      {
        name: 'Yorke Peninsula',
        bounds: { north: -33.8, south: -35.0, east: 138.2, west: 137.2 }
      }
    ]
  },
  {
    state: 'Western Australia',
    country: 'Australia',
    regions: [
      {
        name: 'Coral Coast',
        bounds: { north: -24.0, south: -29.0, east: 116.0, west: 113.0 }
      },
      {
        name: 'Golden Outback',
        bounds: { north: -26.0, south: -34.0, east: 129.0, west: 119.0 }
      },
      {
        name: 'North West',
        bounds: { north: -14.0, south: -24.0, east: 129.0, west: 115.0 }
      },
      {
        name: 'South West',
        bounds: { north: -32.0, south: -35.0, east: 117.0, west: 114.5 }
      },
      {
        name: 'Perth',
        bounds: { north: -31.6, south: -32.4, east: 116.2, west: 115.6 }
      }
    ]
  },
  {
    state: 'Tasmania',
    country: 'Australia',
    regions: [
      {
        name: 'East Coast',
        bounds: { north: -40.8, south: -43.0, east: 148.4, west: 147.5 }
      },
      {
        name: 'Hobart and the South',
        bounds: { north: -42.5, south: -43.6, east: 147.8, west: 146.8 }
      },
      {
        name: 'Launceston and the North',
        bounds: { north: -40.8, south: -41.5, east: 148.4, west: 146.8 }
      },
      {
        name: 'North West',
        bounds: { north: -40.8, south: -41.8, east: 146.8, west: 145.0 }
      },
      {
        name: 'West Coast',
        bounds: { north: -41.8, south: -43.6, east: 146.0, west: 144.6 }
      }
    ]
  },
  {
    state: 'Northern Territory',
    country: 'Australia',
    regions: [
      {
        name: 'Darwin',
        bounds: { north: -12.3, south: -12.6, east: 131.0, west: 130.7 }
      },
      {
        name: 'Top End',
        bounds: { north: -11.0, south: -15.0, east: 134.0, west: 129.0 }
      },
      {
        name: 'Katherine',
        bounds: { north: -14.0, south: -15.0, east: 133.0, west: 131.0 }
      },
      {
        name: 'Central Australia',
        bounds: { north: -20.0, south: -26.0, east: 138.0, west: 129.0 }
      },
      {
        name: 'Alice Springs',
        bounds: { north: -23.5, south: -24.0, east: 134.0, west: 133.5 }
      }
    ]
  },
  {
    state: 'Australian Capital Territory',
    country: 'Australia',
    regions: [
      {
        name: 'Canberra',
        bounds: { north: -35.1, south: -35.5, east: 149.4, west: 148.9 }
      }
    ]
  }
]

// New Zealand - Official 16 Regional Councils
const newZealandRegions: StateRegion[] = [
  {
    state: 'North Island',
    country: 'New Zealand',
    regions: [
      {
        name: 'Northland',
        bounds: { north: -34.4, south: -36.5, east: 174.8, west: 173.0 }
      },
      {
        name: 'Auckland',
        bounds: { north: -36.5, south: -37.2, east: 175.0, west: 174.4 }
      },
      {
        name: 'Waikato',
        bounds: { north: -37.2, south: -38.5, east: 176.0, west: 174.5 }
      },
      {
        name: 'Bay of Plenty',
        bounds: { north: -37.2, south: -38.2, east: 177.5, west: 175.5 }
      },
      {
        name: 'Gisborne',
        bounds: { north: -37.8, south: -38.8, east: 178.5, west: 177.0 }
      },
      {
        name: 'Hawke\'s Bay',
        bounds: { north: -38.8, south: -40.2, east: 177.5, west: 175.8 }
      },
      {
        name: 'Taranaki',
        bounds: { north: -38.5, south: -39.8, east: 175.0, west: 173.7 }
      },
      {
        name: 'Manawatū-Whanganui',
        bounds: { north: -39.0, south: -40.5, east: 176.5, west: 174.0 }
      },
      {
        name: 'Wellington',
        bounds: { north: -40.5, south: -41.8, east: 175.5, west: 174.7 }
      }
    ]
  },
  {
    state: 'South Island',
    country: 'New Zealand',
    regions: [
      {
        name: 'Tasman',
        bounds: { north: -40.7, south: -42.0, east: 173.0, west: 171.8 }
      },
      {
        name: 'Nelson',
        bounds: { north: -41.0, south: -41.8, east: 173.5, west: 172.3 }
      },
      {
        name: 'Marlborough',
        bounds: { north: -41.0, south: -42.2, east: 174.5, west: 172.8 }
      },
      {
        name: 'West Coast',
        bounds: { north: -41.5, south: -44.0, east: 171.5, west: 168.0 }
      },
      {
        name: 'Canterbury',
        bounds: { north: -42.5, south: -45.0, east: 173.0, west: 170.0 }
      },
      {
        name: 'Otago',
        bounds: { north: -44.0, south: -46.5, east: 171.0, west: 168.5 }
      },
      {
        name: 'Southland',
        bounds: { north: -45.5, south: -47.0, east: 169.0, west: 166.0 }
      }
    ]
  }
]

// Combine all regions
const allRegions = [...australianRegions, ...newZealandRegions]

// Function to check if coordinates are within bounds
function isWithinBounds(lat: number, lon: number, bounds: RegionBoundary['bounds']): boolean {
  return lat <= bounds.north && 
         lat >= bounds.south && 
         lon <= bounds.east && 
         lon >= bounds.west
}

// Function to calculate distance between two points
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371 // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

// Function to get center point of a region bounds
function getRegionCenter(bounds: RegionBoundary['bounds']): { lat: number, lon: number } {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lon: (bounds.east + bounds.west) / 2
  }
}

// Function to find the region for given coordinates
export function findRegion(lat: number, lon: number): LocationResult {
  console.log(`🌍 findRegion called with lat=${lat.toFixed(4)}, lon=${lon.toFixed(4)}`)
  
  // First, try to find exact match within bounds
  for (const stateRegion of allRegions) {
    for (const region of stateRegion.regions) {
      if (isWithinBounds(lat, lon, region.bounds)) {
        console.log(`✅ Found exact match: ${region.name}, ${stateRegion.state}, ${stateRegion.country}`)
        return {
          country: stateRegion.country,
          state: stateRegion.state,
          city: region.name,
          region: region.name
        }
      }
    }
  }

  console.log(`🔍 No exact region match found for lat=${lat.toFixed(4)}, lon=${lon.toFixed(4)}, finding closest region...`)

  // If no exact match, find the closest region within the country
  let closestRegion: LocationResult | null = null
  let shortestDistance = Infinity

  // First check if we're clearly in Australia or New Zealand bounds
  const inAustralia = lat > -47 && lat < -10 && lon > 110 && lon < 155
  const inNewZealand = lat > -47 && lat < -34 && lon > 166 && lon < 179

  if (inAustralia || inNewZealand) {
    const targetCountry = inAustralia ? 'Australia' : 'New Zealand'
    
    for (const stateRegion of allRegions) {
      if (stateRegion.country === targetCountry) {
        for (const region of stateRegion.regions) {
          const regionCenter = getRegionCenter(region.bounds)
          const distance = calculateDistance(lat, lon, regionCenter.lat, regionCenter.lon)
          
          if (distance < shortestDistance) {
            shortestDistance = distance
            closestRegion = {
              country: stateRegion.country,
              state: stateRegion.state,
              city: region.name,
              region: region.name
            }
          }
        }
      }
    }
    
    if (closestRegion) {
      console.log(`📍 Closest region found: ${closestRegion.city}, ${closestRegion.state} (${shortestDistance.toFixed(1)}km away)`)
      return closestRegion
    }
  }

  // Fallback for coordinates outside known countries
  if (inAustralia) {
    return {
      country: 'Australia',
      state: 'Victoria', // Default to Victoria for Australian coordinates
      city: 'Unknown',
      region: 'Unknown'
    }
  } else if (inNewZealand) {
    return {
      country: 'New Zealand',
      state: 'North Island', // Default to North Island
      city: 'Unknown',
      region: 'Unknown'
    }
  }

  // Default fallback
  return {
    country: 'Unknown',
    state: 'Unknown',
    city: 'Unknown',
    region: 'Unknown'
  }
}

// Function to get region from route coordinates (tries multiple coordinates for better accuracy)
export function getRouteLocation(coordinates: [number, number][]): LocationResult {
  if (coordinates.length === 0) {
    return {
      country: 'Unknown',
      state: 'Unknown',
      city: 'Unknown',
      region: 'Unknown'
    }
  }

  // Try multiple coordinates along the route for better detection
  const samplesToTry = [
    0,                                    // First coordinate
    Math.floor(coordinates.length / 4),   // 25% through route
    Math.floor(coordinates.length / 2),   // Middle of route
    Math.floor(coordinates.length * 3/4), // 75% through route
    coordinates.length - 1                // Last coordinate
  ]

  for (const index of samplesToTry) {
    if (index < coordinates.length) {
      const [lon, lat] = coordinates[index]
      console.log(`🔍 Checking coordinate ${index}/${coordinates.length}: lat=${lat.toFixed(4)}, lon=${lon.toFixed(4)}`)
      const result = findRegion(lat, lon)
      console.log(`   Result: ${result.city}, ${result.state}, ${result.country}`)
      
      // If we find a known location, return it immediately
      if (result.country !== 'Unknown') {
        console.log(`📍 Location found at coordinate ${index}/${coordinates.length}: ${result.city}, ${result.state}, ${result.country}`)
        return result
      }
    }
  }

  // If no coordinates matched known regions, return unknown
  console.warn('⚠️ No location found for route coordinates - all points outside known regions')
  return {
    country: 'Unknown',
    state: 'Unknown', 
    city: 'Unknown',
    region: 'Unknown'
  }
}

// Utility functions for filter components
export function getAvailableCountries(): string[] {
  const countries = new Set<string>()
  allRegions.forEach(region => countries.add(region.country))
  return Array.from(countries).sort()
}

export function getStatesForCountry(country: string): string[] {
  return allRegions
    .filter(region => region.country === country)
    .map(region => region.state)
    .sort()
}

export function getRegionsForState(state: string): string[] {
  const stateRegion = allRegions.find(region => region.state === state)
  return stateRegion ? stateRegion.regions.map(r => r.name).sort() : []
}

export function getAllRegionData() {
  return {
    countries: getAvailableCountries(),
    states: allRegions.reduce((acc, region) => {
      acc[region.country] = acc[region.country] || []
      if (!acc[region.country].includes(region.state)) {
        acc[region.country].push(region.state)
      }
      return acc
    }, {} as Record<string, string[]>),
    regions: allRegions.reduce((acc, region) => {
      acc[region.state] = region.regions.map(r => r.name).sort()
      return acc
    }, {} as Record<string, string[]>)
  }
}